import { cva, type VariantProps } from 'class-variance-authority';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from './lib/cn';

const pill = cva(
  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill font-medium leading-none transition-colors duration-fast',
  {
    variants: {
      tone: {
        neutral: 'border border-border bg-card text-secondary-foreground',
        brand: 'border border-transparent bg-primary-subtle text-primary-subtle-foreground',
        'brand-solid': 'border border-transparent bg-primary text-primary-foreground',
        success: 'border border-transparent bg-success-subtle text-success',
        warning: 'border border-transparent bg-warning-subtle text-warning',
        danger: 'border border-transparent bg-destructive-subtle text-destructive',
        info: 'border border-transparent bg-info-subtle text-info',
        /** Dark translucent — on the meeting stage or a dark popup. */
        stage: 'border border-white/15 bg-white/10 text-white backdrop-blur-sm',
      },
      size: {
        sm: 'h-5 px-2 text-3xs',
        md: 'h-6 px-2.5 text-sm',
        lg: 'h-7 px-3 text-base',
      },
      interactive: { true: 'cursor-pointer hover:bg-subtle', false: '' },
    },
    defaultVariants: { tone: 'neutral', size: 'md', interactive: false },
  },
);

export interface PillProps extends VariantProps<typeof pill> {
  children: ReactNode;
  icon?: LucideIcon;
  className?: string;
  onClick?: () => void;
}

/** Small rounded label. Status chips, topics, "@mention" chips, meeting badges. */
export function Pill({ children, icon: Icon, tone, size, interactive, className, onClick }: PillProps) {
  const Tag = onClick ? 'button' : 'span';
  return (
    <Tag
      {...(onClick ? { type: 'button' as const, onClick } : {})}
      className={cn(pill({ tone, size, interactive: interactive ?? Boolean(onClick) }), className)}
    >
      {Icon ? <Icon size={size === 'sm' ? 12 : 14} strokeWidth={2} aria-hidden /> : null}
      {children}
    </Tag>
  );
}
